import './slick.scss'
import './slick-theme.scss'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { css, cx, useTheme } from '@fable/theme'
import Slider from 'react-slick'

import { SliderControl } from './SliderControl'
import { BasicSliderProps } from '../types'

export const BasicSlider: React.FC<BasicSliderProps> = ({
  children,
  config,
  containerClass,
  hasControls = false,
  wrapperClass,
  itemCount = 0,
  compact = false,
  activeIndex = 0,
  onChange,
}) => {
  const [isNextHidden, setIsNextHidden] = useState(false)
  const [isPrevHidden, setIsPrevHidden] = useState(false)
  const [dragging, setDragging] = useState(false)
  const sliderRef = useRef<Slider>(null)
  const index = useRef<number>(0)
  const { mediaQueries } = useTheme()

  useEffect(() => {
    if (!sliderRef.current) return

    // activeIndex can be set by parent to jump to a slide
    if (index.current !== activeIndex) {
      index.current = activeIndex
      sliderRef.current.slickGoTo(activeIndex)
    }
  }, [activeIndex])

  const onInit = useCallback(() => {
    if (config.infinite) return

    setIsPrevHidden(true)
  }, [config])

  const beforeChange = useCallback(() => {
    setDragging(true)
  }, [setDragging])

  const afterChange = useCallback(
    (index: number) => {
      setDragging(false)
      onChange && onChange(index)

      if (config.infinite) return

      setIsPrevHidden(index === 0)
      setIsNextHidden(index === itemCount - 1)
    },
    [config, itemCount, setDragging, onChange]
  )

  const handleOnItemClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (dragging) e.stopPropagation()
    },
    [dragging]
  )

  const controls = {
    nextArrow: (
      <SliderControl
        type="next"
        isHidden={isNextHidden}
        compact={compact}
        centerMode={config.centerMode}
      />
    ),
    prevArrow: (
      <SliderControl
        type="prev"
        isHidden={isPrevHidden}
        compact={compact}
        centerMode={config.centerMode}
      />
    ),
  }

  let settings = { ...config, beforeChange, afterChange, onInit }

  if (hasControls && itemCount > 1) {
    settings = { ...settings, ...controls }
  }

  return (
    <div className={wrapperClass}>
      <Slider
        className={cx(
          containerClass,
          !compact &&
            css`
              padding-bottom: 116px;

              ${mediaQueries.mobileL} {
                padding-bottom: 0;
              }
            `,
          'fds-ui--slider'
        )}
        ref={sliderRef}
        {...settings}
      >
        {React.Children.map(children, (child) => (
          <div onClickCapture={handleOnItemClick}>{child}</div>
        ))}
      </Slider>
    </div>
  )
}
